@switch (appNotice?.name) {
  @case ('annual_plans_reminder') {
    @if (withMessageVariant) {
      <span>
        You'll be charged for your annual plan renewal on
        {{ annualRenewalDate }}.
        <a
          href="https://support.callrail.com/"
          target="_blank"
          [class.with-padding]="withPadding">
          <span [class.border-b]="withMockLinks">Contact support</span>
        </a>
        if you want to make changes.
      </span>
    } @else {
      <a
        [crossAppLink]="
          '/settings/a/' + agency.id + appNoticeLinks[appNotice.name]
        "
        [class.with-padding]="withPadding">
        @if (withAlertTitle) {
          <b class="text-domain-brand">{{ noticeTitle }}<br /></b>
        }
        You'll be charged for your annual plan renewal on
        {{ annualRenewalDate }}.
      </a>
    }
  }

  @case ('js_not_installed') {
    <div id="grw-js-dni-page-msg">
      <a
        [crossAppLink]="
          installUrl(company ? company.id : agency.first_company_id)
        "
        class="app-notice notice-unstyled-link"
        [class.with-padding]="withPadding">
        @if (withAlertTitle) {
          <b class="alert-title text-status-error-fg block"
            >{{ noticeTitle }}<br
          /></b>
        }
        <span [class.border-b]="withMockLinks">Add the JavaScript snippet</span>
        to your website to start seeing what pages your leads visited and what
        made them call.
      </a>
    </div>
  }

  @case ('account_disabled') {
    <div id="grw-account-disabled">
      <a
        [crossAppLink]="
          '/settings/a/' + agency.id + appNoticeLinks[appNotice.name]
        "
        [class.with-padding]="withPadding">
        @if (withAlertTitle) {
          <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
        }
        Your account is disabled. To reactivate it,
        <span [class.border-b]="withMockLinks"
          >update your billing information</span
        >.
      </a>
    </div>
  }

  @case ('account_closing') {
    <a
      [crossAppLink]="
        '/settings/a/' + agency.id + appNoticeLinks[appNotice.name]
      "
      [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      Your account is scheduled to close soon. To keep your account open,
      <span [class.border-b]="withMockLinks">visit the Reactivate page</span>.
    </a>
  }

  @case ('trial_pending') {
    <div id="grw-trial-pending">
      <a
        [crossAppLink]="
          (showCCLinkToPending$ | async)
            ? '/settings/a/' + agency.id + appNoticeLinks[appNotice.name]
            : null
        "
        [class.with-padding]="withPadding">
        @if (withAlertTitle) {
          <b>{{ noticeTitle }}<br /></b>
        }
        Welcome! We're waiting for someone to approve your trial.
        @if (showCCLinkToPending$ | async) {
          You can skip this part and get started right away by
          <span [class.border-b]="withMockLinks">entering a credit card</span>.
        }
      </a>
    </div>
  }

  @case ('trial') {
    <div id="grw-trial-period">
      <a
        [crossAppLink]="
          '/settings/a/' + agency.id + appNoticeLinks[appNotice.name]
        "
        [class.with-padding]="withPadding">
        @if (withAlertTitle) {
          <b>{{ noticeTitle }}<br /></b>
        }
        You're currently in your trial period.
        <span [class.border-b]="withMockLinks">Add your credit card</span>
        to keep your lead tracking and settings. You won't be billed until your
        trial ends on {{ agency.next_invoice | date: 'MMM d' }}.
      </a>
    </div>
  }

  @case ('lc_trial') {
    <div id="grw-lc-trial-period">
      <a
        coreSupportLink
        [class.with-padding]="withPadding">
        @if (withAlertTitle) {
          <b>{{ noticeTitle }}<br /></b>
        }
        You're currently in your Lead Center trial period. You will not be
        billed until your trial ends on
        {{ appNotice.meta?.expiration || agency.next_invoice | date: 'MMM d' }}.
        To cancel,
        <span [class.border-b]="withMockLinks">contact our Support team</span>
        before your trial ends.
      </a>
    </div>
  }

  @case ('forms_legacy_upgrade_required') {
    <a
      [crossAppLink]="
        '/settings/a/' + agency.id + appNoticeLinks[appNotice.name]
      "
      [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      Our form tracking pricing is changing. After March 20, you will no longer
      be able to access existing form data or track new form submissions.
      <span [class.border-b]="withMockLinks"
        >Upgrade now to keep tracking forms</span
      >.
    </a>
  }

  @case ('forms_lite_upgrade_required') {
    <a
      [crossAppLink]="
        '/settings/a/' + agency.id + appNoticeLinks[appNotice.name]
      "
      [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      By March 20, you'll need to upgrade to the marketing analytics bundle to
      continue accessing your form tracking data. This pricing bundle also
      includes advanced reporting that ties a cost per lead with each
      {{ 'customer' | verticalLabel: 'customer' }}
      interaction.
      <span [class.border-b]="withMockLinks"
        >Upgrade now to keep tracking forms</span
      >.
    </a>
  }

  @case ('form_capture_limit') {
    <a
      [crossAppLink]="
        '/settings/a/' + agency.id + appNoticeLinks[appNotice.name]
      "
      [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      {{ agency.form_capture_additional_usage }} form submissions aren't showing
      because you've reached your plan limit.
      <span [class.border-b]="withMockLinks"
        >Upgrade to increase your limit and see all of your form
        submissions</span
      >.
    </a>
  }

  @case ('billing') {
    <div id="grw-failed-payment">
      <a
        [crossAppLink]="
          '/settings/a/' + agency.id + appNoticeLinks[appNotice.name]
        "
        [class.with-padding]="withPadding">
        @if (withAlertTitle) {
          <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
        }
        Your most recent credit card payment has failed.
        <span [class.border-b]="withMockLinks"
          >Update your billing information</span
        >
        before {{ agency.disable_for_nonpayment_on | date: 'longDate' }}
        to avoid interruption in your service.
      </a>
    </div>
  }

  @case ('manual_invoice_late') {
    <div id="grw-failed-payment">
      <a
        [crossAppLink]="
          '/settings/a/' + agency.id + appNoticeLinks[appNotice.name]
        "
        [class.with-padding]="withPadding">
        @if (withAlertTitle) {
          <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
        }
        Your most recent invoice is past its due date. Please visit our
        <span [class.border-b]="withMockLinks">billing page</span>
        to review your invoices and send payment to avoid interruption in your
        service.
      </a>
    </div>
  }

  @case ('no_trackers') {
    <a
      [crossAppLink]="'/settings/a/' + agency.id + '/routing/account-numbers'"
      [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      Your account is active, but you don't have any active phone numbers. Would
      you like to
      <span [class.border-b]="withMockLinks">re-enable your numbers</span>
      @if (company) {
        or create a new tracking phone number
      }
      ?
    </a>
  }

  @case ('api_v3') {
    <a
      href="https://support.callrail.com/hc/en-us/articles/5711821845389-CallRail-s-API-documentation"
      target="_blank"
      [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b>{{ noticeTitle }}<br /></b>
      }
      <span [class.border-b]="withMockLinks">API v3</span> is now available! If
      you're still using v1 or v2, update your code to access our new API
      endpoints before August 16th.
    </a>
  }

  @case ('no_forms_product') {
    <a
      [crossAppLink]="
        '/settings/a/' +
        agency.id +
        appNoticeLinks[appNotice.name] +
        '?open_modal=true'
      "
      [coreGaEvent]="['Cross Sell', 'Top Level Alert', 'Forms Trial']"
      [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b>{{ noticeTitle }}<br /></b>
      }
      Now CallRail can track your web forms too.
      <span [class.border-b]="withMockLinks">Try it for free</span>.
    </a>
  }

  @case ('risky_swap') {
    <a
      class="notice-unstyled-link"
      [crossAppLink]="
        '/settings/a/' +
        agency.id +
        '/routing/calls-and-texts/numbers/' +
        appNotice.meta?.tracker_id +
        '/number-assignments?company_id=' +
        appNotice.meta?.company_id
      "
      [coreGaEvent]="['App Notice', 'click', 'Risky Swap']"
      [class.with-padding]="withPadding"
      (click)="markViewed()">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      Numbers in <b>{{ appNotice.meta?.tracker_name }}</b> for your company
      <b>{{ appNotice.meta?.company_name }}</b>
      are swapping too fast. Your leads may not be tracking correctly.

      <ng-container *ngTemplateOutlet="timestamp"></ng-container>
    </a>
  }

  @case ('number_not_swapping') {
    <a
      class="notice-unstyled-link"
      [crossAppLink]="
        '/settings/a/' +
        agency.id +
        '/routing/calls-and-texts/numbers/' +
        appNotice.meta?.tracker_id +
        '/number-assignments?company_id=' +
        appNotice.meta?.company_id
      "
      [coreGaEvent]="['App Notice', 'click', 'Number Not Swapping']"
      [class.with-padding]="withPadding"
      (click)="markViewed()">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      {{ appNotice.meta?.tracker_name }} has not swapped in the past 5 days. You
      may not be receiving visitor traffic data accurately.
      <ng-container *ngTemplateOutlet="timestamp"></ng-container>
    </a>
  }

  @case ('snippet_installation') {
    <a
      class="notice-unstyled-link"
      [crossAppLink]="installUrl(appNotice.meta?.company_id)"
      [coreGaEvent]="['App Notice', 'click', 'Snippet Instalation']"
      [class.with-padding]="withPadding"
      (click)="markViewed()">
      The JS snippet for your company
      <b>{{ appNotice.meta?.company_name }}</b> may not be installed correctly.
      Your leads may not be tracking correctly.<br />
      <ng-container *ngTemplateOutlet="timestamp"></ng-container>
    </a>
  }

  @case ('redundant_plans') {
    <a
      class="notice-unstyled-link"
      [crossAppLink]="
        '/settings/a/' + agency.id + appNoticeLinks[appNotice.name]
      "
      [coreGaEvent]="['App Notice', 'click', 'Redundant Plans']"
      [class.with-padding]="withPadding"
      (click)="markViewed()">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      @for (planName of appNotice.meta?.redundant_plans; track planName) {
        <b>{{ planName }}</b> has features that your existing plans already
        cover. Please consider removing this plan.
      }
      <span [class.border-b]="withMockLinks">Visit your billing page</span> to
      review your plans.
    </a>
  }
}

@if (appNotice?.category === 'lead center') {
  @if (appNotice.name === 'tendlc_registration__high') {
    <div [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      You've reached the limit of cold outbound texts you can send until you
      complete outbound SMS registration.
    </div>
  }

  @if (appNotice.name === 'tendlc_registration__mid') {
    <div [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      You're approaching your limit of cold outbound texts available until you
      complete outbound SMS registration for 10DLC compliance.
    </div>
  }

  @if (appNotice.name === 'tendlc_registration__low') {
    <div [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      You will be able to send a set limit of cold outbound texts until you
      complete outbound SMS registration for 10DLC compliance.
    </div>
  }

  @if (appNotice.name === 'tendlc_registration__high__admin') {
    <a
      href="https://support.callrail.com/hc/en-us/articles/5712060908045-Text-Messaging-Compliance-Registration"
      target="_blank"
      [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      You've reached the limit of cold outbound texts you can send until you
      complete outbound SMS registration.
      <span [class.border-b]="withMockLinks">Begin registration here</span>.
    </a>
  }

  @if (appNotice.name === 'tendlc_registration__mid__admin') {
    <a
      href="https://support.callrail.com/hc/en-us/articles/5712060908045-Text-Messaging-Compliance-Registration"
      target="_blank"
      [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      You're approaching your limit of cold outbound texts available until you
      complete outbound SMS registration for 10DLC compliance.
      <span [class.border-b]="withMockLinks">Begin registration here</span>.
    </a>
  }

  @if (appNotice.name === 'tendlc_registration__low__admin') {
    <a
      href="https://support.callrail.com/hc/en-us/articles/5712060908045-Text-Messaging-Compliance-Registration"
      target="_blank"
      [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      You will be able to send a set limit of cold outbound texts until you
      complete outbound SMS registration for 10DLC compliance.
      <span [class.border-b]="withMockLinks">Begin registration here</span>.
    </a>
  }
}

@if (appNotice?.category === 'integration') {
  @if (appNotice.name === 'pending') {
    <a
      class="notice-unstyled-link"
      [crossAppLink]="
        '/settings/a/' +
        agency.id +
        '/settings/integrations/' +
        appNotice.config?.ng_route +
        '?company_id=' +
        appNotice.config?.company_id
      "
      [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b class="text-status-error-fg">{{ noticeTitle }}<br /></b>
      }
      <b>{{ appNotice.config?.integration }}</b> is <b>incomplete</b>

      @if (agency.active_company_count > 1 && appNotice.config?.company_name) {
        for <b>{{ appNotice.config?.company_name }}</b>
      }
      . Finish the setup process to activate.

      <ng-container *ngTemplateOutlet="timestamp"></ng-container>
    </a>
  }

  @if (appNotice.name === 'failed') {
    <a
      class="notice-unstyled-link"
      [crossAppLink]="
        '/settings/a/' +
        agency.id +
        '/settings/integrations/' +
        appNotice.config?.ng_route +
        '?company_id=' +
        appNotice.config?.company_id
      "
      [class.with-padding]="withPadding">
      @if (withAlertTitle) {
        <b class="text-valencia">{{ noticeTitle }}<br /></b>
      }
      <b>{{ appNotice.config?.integration }}</b> has <b>failed</b>

      @if (agency.active_company_count > 1 && appNotice.config?.company_name) {
        for <b>{{ appNotice.config?.company_name }}</b>
      }
      . Visit the integration page to see the problem and reactivate.

      <ng-container *ngTemplateOutlet="timestamp"></ng-container>
    </a>
  }
}

@if (appNotice?.category === 'general') {
  <a
    class="notice-unstyled-link"
    crossAppLink="/settings/a/{{
      appNotice.meta.agency_id
    }}/workflow/teams?company_id={{ appNotice.meta.company_id }}"
    [class.with-padding]="withPadding">
    {{ appNotice.meta?.message }}

    <ng-container *ngTemplateOutlet="timestamp"></ng-container>
  </a>
}

<ng-template #timestamp>
  <span class="block whitespace-nowrap text-gray-600">
    <ui-time-ago
      [timestamp]="appNotice?.created_at"
      case="sentence"></ui-time-ago>
  </span>
</ng-template>
